import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { PanelSimulator } from "@/components/PanelSimulator";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function PanelSimulatorPage() {
  useDocumentMeta({
    title: "FACU Panel Simulator — Lab",
    description: "Interactive fire alarm control unit simulator. Practice acknowledging alarms, silencing, resetting, drills, and supervisory conditions. 10 NICET-aligned guided scenarios.",
    path: "/lab/panel",
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="panel-page">
        <div className="overline mb-3"><span className="red-bar" />Lab · FACU Simulator</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mb-3">
          Fire alarm control panel simulator.
        </h1>
        <p className="text-zinc-700 max-w-2xl leading-relaxed mb-7">
          Drill the response sequences you'll perform on real systems. The simulator models a generic
          addressable FACU (APM-7200) with industry-standard ACK / SILENCE / RESET / DRILL / TEST controls,
          discrete LED states, and an LCD event display.
        </p>

        <PanelSimulator />

        <div className="mt-10 card-tech p-5">
          <div className="overline mb-2"><span className="red-bar" />Tips</div>
          <ul className="text-sm text-zinc-700 space-y-2 leading-relaxed">
            <li><strong>Sound:</strong> Click the SOUND button on the panel to enable audio — alarms play the NFPA Temporal-3 horn pattern, trouble signals chirp every 4 sec, supervisory uses a two-beep pattern, and the panel piezo buzzes continuously. Click again to mute.</li>
            <li><strong>ACK</strong> only silences the panel piezo — it does NOT silence horns/strobes. Common student mistake.</li>
            <li><strong>SILENCE</strong> turns off NACs but the alarm condition stays latched. Use only after FD command.</li>
            <li><strong>RESET</strong> requires the initiating device to be restored — if the smoke is still in chamber, alarm immediately returns.</li>
            <li><strong>DRILL</strong> activates NACs but logs the event as "drill" not "alarm" — does not transmit to the supervising station as a real alarm.</li>
            <li><strong>TEST</strong> mode bypasses NAC + central-station for the selected zone during ITM. Other zones remain live.</li>
            <li>Priority order on the display: <span className="font-mono">ALARM &gt; SUPERVISORY &gt; TROUBLE</span> per NFPA 72 §10.16. Use CYCLE EVENTS to walk all queued conditions.</li>
          </ul>
        </div>

        <div className="mt-8 text-sm text-zinc-600">
          <Link to="/lab" className="hover:text-red-600" data-testid="panel-back-link">
            ← Back to lab
          </Link>
        </div>
      </main>
    </div>
  );
}
